import { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";

const inputCls = "w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]";
const labelCls = "block text-sm font-semibold text-gray-700 mb-1";

export default function DisbursementEmailDialog({ open, onOpenChange, clientId, clientName, clientEmail }) {
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTo(clientEmail || "");
    setCc("");
    setSubject(`Settlement disbursement — ${clientName || "your matter"}`);
    setBody("");
  }, [open, clientEmail, clientName]);

  const send = async () => {
    if (!to.trim()) { toast.error("Add at least one recipient"); return; }
    setSending(true);
    try {
      await api.post(`/clients/${clientId}/disbursement/email`, {
        to: [to], cc: cc ? [cc] : [], subject, body,
      });
      toast.success("Disbursement statement emailed");
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send email");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-sm" data-testid="disbursement-email-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Email disbursement statement</DialogTitle>
          <DialogDescription className="sr-only">Send the disbursement PDF as an email attachment.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-1">
          <div>
            <label className={labelCls}>To</label>
            <input data-testid="disb-email-to" value={to} onChange={(e) => setTo(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Cc <span className="font-normal text-gray-400">(optional)</span></label>
            <input data-testid="disb-email-cc" value={cc} onChange={(e) => setCc(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Subject</label>
            <input data-testid="disb-email-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Message <span className="font-normal text-gray-400">(optional)</span></label>
            <textarea data-testid="disb-email-body" value={body} onChange={(e) => setBody(e.target.value)} rows={4} className={inputCls}
              placeholder="Leave blank to use a default message." />
          </div>
        </div>
        <DialogFooter className="mt-4">
          <button onClick={() => onOpenChange(false)} className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-sm font-semibold text-sm">Cancel</button>
          <button onClick={send} disabled={sending} data-testid="disb-email-send"
            className="bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm flex items-center gap-2 disabled:opacity-60">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
